// The New Relic browser agent wraps console.* itself, but only records the FIRST
// argument of each call - `console.log('label:', value)` arrives as just 'label:'.
// This forwarder replaces the console methods so that every argument is serialized
// into one message and sent through the agent's log API instead.

type ConsoleMethod = 'log' | 'info' | 'warn' | 'error' | 'debug' | 'trace';
type LogLevel = 'INFO' | 'WARN' | 'ERROR' | 'DEBUG' | 'TRACE';

export type NativeConsole = Record<ConsoleMethod, (...args: any[]) => void>;

interface LogAgent {
  log: (message: string, options?: { level?: LogLevel; customAttributes?: Record<string, any> }) => void;
}

const LEVELS: Record<ConsoleMethod, LogLevel> = {
  log: 'INFO',
  info: 'INFO',
  warn: 'WARN',
  error: 'ERROR',
  debug: 'DEBUG',
  trace: 'TRACE',
};

const MAX_MESSAGE_LENGTH = 8000;

// Must be called BEFORE the New Relic agent is created, so the saved methods are
// the browser's own and not the agent's wrappers.
export const captureNativeConsole = (): NativeConsole => {
  const methods = Object.keys(LEVELS) as ConsoleMethod[];
  return methods.reduce((acc, method) => {
    acc[method] = console[method].bind(console);
    return acc;
  }, {} as NativeConsole);
};

const describeError = (error: Error) => {
  const code = (error as any).code !== undefined ? ` (code: ${(error as any).code})` : '';
  const header = `${error.name}: ${error.message}${code}`;
  if (!error.stack) return header;
  // Chromium stacks already start with "name: message", WebKit/Gecko stacks don't.
  return error.stack.startsWith(`${error.name}: ${error.message}`)
    ? `${header}${error.stack.slice(`${error.name}: ${error.message}`.length)}`
    : `${header}\n${error.stack}`;
};

const describeElement = (element: Element) => {
  const id = element.id ? `#${element.id}` : '';
  const classes = typeof element.className === 'string' && element.className
    ? `.${element.className.trim().split(/\s+/).join('.')}`
    : '';
  return `<${element.tagName.toLowerCase()}${id}${classes}>`;
};

const stringify = (value: any) => {
  const seen = new WeakSet();
  return JSON.stringify(value, (_key, nested) => {
    if (nested instanceof Error) return { name: nested.name, message: nested.message, code: (nested as any).code };
    if (typeof Element !== 'undefined' && nested instanceof Element) return describeElement(nested);
    if (typeof nested === 'function') return `[Function ${nested.name || 'anonymous'}]`;
    if (typeof nested === 'bigint') return nested.toString();
    if (nested && typeof nested === 'object') {
      // OpenTok events and SDK objects are full of circular references.
      if (seen.has(nested)) return '[Circular]';
      seen.add(nested);
    }
    return nested;
  });
};

const formatArg = (arg: any): string => {
  if (typeof arg === 'string') return arg;
  if (arg instanceof Error) return describeError(arg);
  if (typeof Element !== 'undefined' && arg instanceof Element) return describeElement(arg);
  if (arg === null || typeof arg !== 'object') return String(arg);
  try {
    return stringify(arg) ?? String(arg);
  } catch {
    return String(arg);
  }
};

export const formatConsoleArgs = (args: any[]): string => {
  let rest = args;
  let head: string | null = null;

  // Drop "%c" styling markers along with the CSS argument each one consumes
  // (OpenTok logs as '%cOpenTok:...%c', 'color: ...', 'color: ...', message).
  if (typeof args[0] === 'string' && args[0].includes('%c')) {
    const styleCount = (args[0].match(/%c/g) || []).length;
    head = args[0].replace(/%c/g, '');
    rest = args.slice(1 + styleCount);
  }

  const parts = rest.map(formatArg);
  if (head !== null) parts.unshift(head);

  const message = parts.join(' ');
  return message.length > MAX_MESSAGE_LENGTH
    ? `${message.slice(0, MAX_MESSAGE_LENGTH)}... [truncated ${message.length - MAX_MESSAGE_LENGTH} chars]`
    : message;
};

export const forwardConsoleToNewRelic = (agent: LogAgent, nativeConsole: NativeConsole) => {
  // The agent reports its own warnings through console.debug; without this guard a
  // warning raised while logging would be forwarded again, and again.
  let forwarding = false;

  (Object.keys(LEVELS) as ConsoleMethod[]).forEach((method) => {
    console[method] = (...args: any[]) => {
      // Keep the original arguments in DevTools so objects stay inspectable.
      nativeConsole[method](...args);

      if (forwarding || args.length === 0) return;
      forwarding = true;
      try {
        agent.log(formatConsoleArgs(args), { level: LEVELS[method] });
      } catch {
        // Logging must never break the app.
      } finally {
        forwarding = false;
      }
    };
  });
};
